import { baseApi as api } from './baseApi';

/* ── Tipos ──────────────────────────────────────────────────────────────── */

export interface ChatParticipant {
  _id:      string;
  name:     string;
  lastName: string;
  avatar?:  string;
  role:     string;
  /**
   * Viene solo en el buscador de "Nueva conversación", que ahora también lista a los
   * dados de baja. Ocultarlos parecía prolijo hasta que hubo que escribirle a uno: un
   * admin veía en el panel del Agente Virtual que una consulta estaba en manos de alguien
   * inactivo y no podía ni preguntarle qué hacer con ella. (09/09/2026)
   */
  isActive?: boolean;
}

export interface LastMessage {
  text:     string;
  senderId: string;
  sentAt:   string;
}

export interface Conversation {
  _id:          string;
  type:         'direct' | 'group';
  participants: ChatParticipant[];
  name?:        string;
  avatar?:      string;
  createdBy:    string;
  admins:       string[];
  lastMessage?: LastMessage;
  unread:       number;
  updatedAt:    string;
}

export interface MessageReaction { emoji: string; userIds: string[]; }
export interface ReplyRef { messageId: string; senderName: string; contentPreview: string; }

export interface ChatMessage {
  _id:            string;
  conversationId: string;
  /**
   * Puede venir NULL: si el usuario que escribió fue borrado, la populate del backend
   * no resuelve nada. Pasa con los mensajes de la Bolsa entre tenants. Tratarlo como
   * siempre presente tumbaba la pantalla entera del chat. (bug 31/07)
   */
  sender:         ChatParticipant | null;
  type:           'text' | 'image' | 'file' | 'call';
  content:        string;
  /** Solo en type='call': la llamada del chat y cómo terminó (se actualiza en vivo). */
  call?: {
    callId:       string;
    kind:         'direct' | 'group';
    status:       'ringing' | 'ongoing' | 'ended' | 'missed' | 'rejected' | 'cancelled';
    durationSec?: number;
    joinedCount?: number;
  };
  fileName?:      string;
  replyTo?:       ReplyRef | null;
  reactions:      MessageReaction[];
  readBy:         { userId: string; readAt: string }[];
  deleted:        boolean;
  createdAt:      string;
}

export interface MessagePage {
  data:       ChatMessage[];
  pagination: { total: number; page: number; limit: number; totalPages: number };
}

/* ── API ────────────────────────────────────────────────────────────────── */

export const chatApi = api.injectEndpoints({
  endpoints: (build) => ({

    /**
     * De acá sale el contador de no leídos del sidebar, así que es la query que no puede
     * quedar vieja. `refetchOnFocus` / `refetchOnReconnect` NO se declaran acá —en esta
     * versión de RTK Query no son opciones del endpoint y TypeScript las rechaza—: van
     * en cada `useListConversationsQuery(...)`, ver el Sidebar.
     */
    listConversations: build.query<Conversation[], void>({
      query: () => '/chat',
      transformResponse: (r: any) => r.data,
      providesTags: ['Chat'],
    }),

    getOrCreateDirect: build.mutation<Conversation, string>({
      query: (userId) => ({ url: `/chat/direct/${userId}`, method: 'POST' }),
      transformResponse: (r: any) => r.data,
      invalidatesTags: ['Chat'],
    }),

    createGroup: build.mutation<Conversation, { name: string; participantIds: string[]; avatar?: string }>({
      query: (body) => ({ url: '/chat/group', method: 'POST', body }),
      transformResponse: (r: any) => r.data,
      invalidatesTags: ['Chat'],
    }),

    addGroupMembers: build.mutation<Conversation, { id: string; memberIds: string[] }>({
      query: ({ id, memberIds }) => ({ url: `/chat/${id}/members`, method: 'POST', body: { memberIds } }),
      transformResponse: (r: any) => r.data,
      invalidatesTags: ['Chat'],
    }),

    removeGroupMember: build.mutation<{ ok: boolean }, { id: string; memberId: string }>({
      query: ({ id, memberId }) => ({ url: `/chat/${id}/members/${memberId}`, method: 'DELETE' }),
      transformResponse: (r: any) => r.data,
      invalidatesTags: ['Chat'],
    }),

    getMessages: build.query<MessagePage, { id: string; page?: number }>({
      query: ({ id, page = 1 }) => ({ url: `/chat/${id}/messages`, params: { page, limit: 40 } }),
      transformResponse: (r: any) => r.data,
      providesTags: (_res, _err, { id }) => [{ type: 'ChatMessages', id }],
    }),

    sendMessage: build.mutation<ChatMessage, { id: string; content: string; type?: string; fileName?: string; replyTo?: string }>({
      query: ({ id, ...body }) => ({ url: `/chat/${id}/messages`, method: 'POST', body }),
      transformResponse: (r: any) => r.data,
      invalidatesTags: (_res, _err, { id }) => [{ type: 'ChatMessages', id }, 'Chat'],
    }),

    uploadChatFile: build.mutation<{ url: string; type: 'image' | 'file'; fileName: string; mime: string }, FormData>({
      query: (body) => ({ url: '/chat/upload', method: 'POST', body }),
      transformResponse: (r: any) => r.data,
    }),

    reactMessage: build.mutation<ChatMessage, { messageId: string; conversationId: string; emoji: string }>({
      query: ({ messageId, emoji }) => ({ url: `/chat/messages/${messageId}/react`, method: 'POST', body: { emoji } }),
      transformResponse: (r: any) => r.data,
      invalidatesTags: (_res, _err, { conversationId }) => [{ type: 'ChatMessages', id: conversationId }],
    }),

    deleteConversation: build.mutation<{ deleted: boolean; conversationId: string }, string>({
      query: (id) => ({ url: `/chat/${id}`, method: 'DELETE' }),
      transformResponse: (r: any) => r.data,
      invalidatesTags: (_res, _err, id) => [{ type: 'ChatMessages', id }, 'Chat'],
    }),

    markRead: build.mutation<{ ok: boolean }, string>({
      query: (id) => ({ url: `/chat/${id}/read`, method: 'POST' }),
      transformResponse: (r: any) => r.data,
      invalidatesTags: ['Chat'],
    }),

    searchChatUsers: build.query<ChatParticipant[], string>({
      query: (q) => ({ url: '/chat/users/search', params: { q } }),
      transformResponse: (r: any) => r.data,
    }),

    sendTyping: build.mutation<{ ok: boolean }, string>({
      query: (id) => ({ url: `/chat/${id}/typing`, method: 'POST' }),
      transformResponse: (r: any) => r.data,
    }),
  }),
});

export const {
  useListConversationsQuery,
  useGetOrCreateDirectMutation,
  useCreateGroupMutation,
  useAddGroupMembersMutation,
  useRemoveGroupMemberMutation,
  useGetMessagesQuery,
  useSendMessageMutation,
  useUploadChatFileMutation,
  useReactMessageMutation,
  useDeleteConversationMutation,
  useMarkReadMutation,
  useSearchChatUsersQuery,
  useSendTypingMutation,
} = chatApi;
